import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import moment, { Moment } from 'moment';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { EdgeApi } from '@trialcard/apigateway.client';
import SessionModule from './session';
import { Settings } from '~/services/settings';
import { formatAddress } from '~/services/utils';

interface IAddress {
    addressOne: string;
    addressTwo?: string;
    city: string;
    state: string;
    zip: string;
}

interface ICaregiver {
    firstName: string;
    lastName: string;
    dateOfBirth?: Moment | null | string;
    relationshipToPatient: string;
    phoneNumber: string;
    email: string;
}

interface IPrescriber {
    firstName: string;
    middleName?: string;
    lastName: string;
    siteName: string;
    officeContactName: string;
    prescriberNpi: string;
    prescriberTaxId: string;
    prescriberPtan: string;
    addressLineOne: string;
    addressLineTwo?: string;
    prescriberZipCode?: string;
    prescriberCity?: string;
    prescriberState?: string;
    officeContactPhone?: string;
    officeContactFax?: string;
}

interface IPrescriberAPI {
    deaNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    npi: string;
    ptan: string;
    stateLicenseId: string;
    suffix: string;
    taxId: string;
    type: string;
}

interface IPharmacyPayorApi {
    bin: string;
    pcn: string;
    insuranceState: string;
    pbmName: string;
    rxMemberId: string;
    payorId: string;
    payorName: string;
    phoneNumber: string;
    faxNumber: string;
    groupNumber: string;
    policyNumber: string;
    planEffectiveDate: string;
    planExpirationDate: string;
    planTerminationDate: string;
    policyName: string;
    policyHolderFirstName: string;
    policyHolderLastName: string;
    insurancePriority: string;
    relationshipToPatient: string;
    address?: IAddress | {};
}

interface ISiteApi {
    id: string | number;
    name: string;
    contactName?: string;
    phoneNumber?: string;
    faxNumber?: string;
    description?: string;
    type?: string;
    address?: IAddress | {};
}

export interface IPrescription {
    id?: string;
    daySupply?: string;
    directions?: string;
    frequency?: number;
    ndc?: string;
    quantity?: number;
    refills?: number;
    writtenOn?: string;
}

interface PatientInformation {
    IsPatientAgeBetween6And17: string;
    firstName: string;
    middleName: string;
    lastName: string;
    dateOfBirth: Moment | null | string;
    gender: string;
    addressOne: string;
    addressTwo: string;
    zip: string;
    city: string;
    state: string;
    patientPhone: string;
    patientHomePhone: string;
    email: string;
    prefferedPhone: string;
    caregiverFirstName: string;
    relationShipToPatient: string;
    caregiverPhone: string;
    caregiverHomePhone: string;
    caregiverEmail: string;
    todaysDate: string;
}

interface HealthInsuranceInformation {
    hasMedicalInsurance: boolean | null;
    hasPharmacyInsurance: boolean | null;
    // hasNoInsurance: boolean | null;
    // medicalFirstName: string;
    // medicalLastName: string;
    // medicalInsuranceName: string;
    // medicalInsuranceId: string;
    // medicalGroupNumber: string;
    uploadMedicareCardTypeA: boolean | null;
    uploadMedicareCardTypeB: boolean | null;
    uploadMedicareCardTypeD: boolean | null;
    rxInsurance: string; // Used as variable for Prescription Insurance text field
    memberName: string;
    memberId: string;
    insuranceCompanyPhone: string;
    rxCardName: string;
    rxGroup: string;
    rxId: string;
    rxBin: string;
    rxPcn: string;
}

interface DiagnosisInformation {
    selectedDiagnoses: string[];
    infusionLocation: string | null;
    infusionSiteName: string;
    hospitalAffiliation: string;
    siteStreetAddress: string;
    siteSuite: string;
    siteCity: string;
    siteState: string;
    siteZipCode: string;
    siteContactName: string;
    siteContactPhone: string;
    siteContactEmailAddress: string;
    siteContactFax: string;
}
export interface PharmacyPrescriptionInformation {
    preferredPharmacy?: string;
    patientWeight?: string;
    dateOfRecord?: Moment | null | string;
    prescriptionFor?: string;
    prescriberSignature?: string;
    allPrescriptions?: IPrescription[];
    todaysDate?: string;
    writtenOn?: string;
}

interface GettingStarted {
    newPatient: null | boolean;
    restartingPatient: null | boolean;
    switchingPatient: null | boolean;
    allSupportServices: null | boolean;
    benefitsInvestigation: null | boolean;
    copayServices: null | boolean;
    injectionSupport: null | boolean;
    glatopaInject: null | boolean;
    otherMedications: null | boolean;
    allergies: null | boolean;
    language: string | null;
    otherBrandName: string | null;
    allergiesName: string | null;
    otherMedicationsName: string | null;
}

enum SupportTypes {
    BenefitsInvestigation = 'BI',
    Copay = 'Co-pay',
    InjectionSupport = 'Injection Training',
    Glatopaject = 'Glatopaject® Device Request (Device Only)',
}

interface IAnswer {
    key: string;
    value: string | boolean;
}

interface IQuestion {
    key: string;
    answers: IAnswer[];
}

interface ISurvey {
    key: string;
    questions: IQuestion[];
}

interface ISurveyData {
    surveys: ISurvey[];
}

interface ISocSiteApi {
    contactName: string;
    description: string;
    faxNumber: string;
    id: string;
    name: string;
    phoneNumber: string;
    type: string;
    address?: IAddress;
}

interface IMedicalPayorApi {
    payorId: string;
    payorName: string;
    phoneNumber: string;
    faxNumber: string;
    groupNumber: string;
    policyNumber: string;
    planEffectiveDate: string;
    planExpirationDate: string;
    planTerminationDate: string;
    policyName: string;
    policyHolderFirstName: string;
    policyHolderLastName: string;
    insurancePriority: string;
    relationshipToPatient: string;
    address?: IAddress | {};
}

export const NDC_MAP: Record<string, string> = {
    '45 mg': '61314065101',
    '90 mg': '61314065201',
    '260 mg dose': '61314065494',
    '390 mg dose': '61314065494',
    '520 mg dose': '61314065494',
};

@InjectModule({ stateFactory: true }, module)
export default class PatientSupportServices extends InjectVuexModule {
    @Resolve
    public session!: SessionModule;

    @Resolve
    public settings!: Settings;

    @Resolve
    public edgeApi!: EdgeApi;

    public isPatientAdult: string | undefined = '';

    public defaultGettingStarted = {
        newPatient: false,
        restartingPatient: false,
        switchingPatient: false,
        allSupportServices: false,
        benefitsInvestigation: false,
        copayServices: false,
        injectionSupport: false,
        glatopaInject: false,
        otherMedications: false,
        allergies: false,
        language: '',
        otherBrandName: '',
        allergiesName: '',
        otherMedicationsName: '',
    };

    public defaultPatientInformationData = {
        IsPatientAgeBetween6And17: '' as string,
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: null as Moment | string | null,
        gender: '',
        addressOne: '',
        addressTwo: '',
        zip: '',
        city: '',
        state: '',
        patientPhone: '',
        patientHomePhone: '',
        email: '',
        prefferedPhone: '',
        caregiverFirstName: '',
        relationShipToPatient: '',
        caregiverPhone: '',
        caregiverHomePhone: '',
        caregiverEmail: '',
        todaysDate: moment.utc().format('MM/DD/YYYY'),
    };

    public defaultHealthInformationData = {
        hasPharmacyInsurance: null as boolean | null,
        hasMedicalInsurance: null as boolean | null,
        uploadMedicareCardTypeA: false as boolean,
        uploadMedicareCardTypeB: false as boolean,
        uploadMedicareCardTypeD: false as boolean,
        memberName: '' as string,
        memberId: '' as string,
        insuranceCompanyPhone: '' as string,
        rxCardName: '' as string,
        rxInsurance: '',
        rxGroup: '',
        rxId: '',
        rxBin: '',
        rxPcn: '',
    };

    public defaultDiagnosisInformationData = {
        selectedDiagnoses: [] as string[],
        infusionLocation: null as string | null,
        infusionSiteName: '',
        hospitalAffiliation: '',
        siteStreetAddress: '',
        siteSuite: '',
        siteCity: '',
        siteState: '',
        siteZipCode: '',
        siteContactName: '',
        siteContactPhone: '',
        siteContactEmailAddress: '',
        siteContactFax: '',
    };

    public defaultPrescriberInformation = {
        firstName: '',
        lastName: '',
        siteName: '',
        officeContactName: '',
        prescriberNpi: '',
        prescriberTaxId: '',
        prescriberPtan: '',
        addressLineOne: '',
        addressLineTwo: '',
        prescriberZipCode: '',
        prescriberCity: '',
        prescriberState: '',
        officeContactPhone: '',
        officeContactFax: '',
    };

    public defaultPharmacyPrescriptionInformationData = {
        preferredPharmacy: '',
        patientWeight: '',
        dateOfRecord: null as Moment | string | null,
        prescriptionFor: '',
        prescriberSignature: '',
        allPrescriptions: [] as IPrescription[],
        todaysDate: moment.utc().format('MM/DD/YYYY'),
    };

    public defaultPrescriberSignAndDate = {
        prescriberSignature: '',
        todaysDate: moment.utc().format('MM/DD/YYYY'),
    };

    public defaultSurveys = [];

    public gettingStarted: GettingStarted = { ...this.defaultGettingStarted };
    public patientInformation = { ...this.defaultPatientInformationData };
    public healthInsuranceInformation: HealthInsuranceInformation = { ...this.defaultHealthInformationData };
    public diagnosisInformation = { ...this.defaultDiagnosisInformationData };
    public prescriberInformation: IPrescriber = { ...this.defaultPrescriberInformation };
    public pharmacyPrescriptionInformation: PharmacyPrescriptionInformation = { ...this.defaultPharmacyPrescriptionInformationData };

    public surveys: ISurvey[] = [...this.defaultSurveys];

    public prescriberSignAndDate = {...this.defaultPrescriberSignAndDate};

    @Mutation
    public updateGettingStartedData(payload: GettingStarted) {
        this.gettingStarted = payload;
    }

    @Mutation
    public updatePatientInformation(payload: PatientInformation) {
        this.patientInformation = payload;
    }

    @Mutation
    public updateHealthInsuranceInformation(payload: HealthInsuranceInformation) {
        this.healthInsuranceInformation = payload;
    }

    @Mutation
    public updateDiagnosisInformation(payload: DiagnosisInformation | any) {
        this.diagnosisInformation = payload;
    }

    @Mutation
    public updatePrescriberInformation(payload: IPrescriber) {
        this.prescriberInformation = payload;
    }

    @Mutation
    public updatePharmacyPrescriptionInformation(payload: PharmacyPrescriptionInformation) {
        this.pharmacyPrescriptionInformation = payload;
    }

    @Mutation
    public updateIsPatientAdult(payload: string | undefined) {
        this.isPatientAdult = payload;
    }

    @Mutation public updateSurveys(payload: ISurvey[]) {
        this.surveys = payload
    }

    @Mutation
    public addSurvey(payload: { surveyKey: string; questions: IQuestion[] }): void {
        const { surveyKey, questions } = payload;
        if (!payload || !questions) return;
        const existingSurveyIndex = this.surveys.findIndex(s => s.key === surveyKey);
        if (existingSurveyIndex !== -1) {
            this.surveys[existingSurveyIndex] = { key: surveyKey, questions };
        } else {
            this.surveys.push({ key: surveyKey, questions });
        }
    }

    @Mutation
    public updateSignAndDate(payload : {prescriberSignature : string, todaysDate : string}):void {
        this.prescriberSignAndDate = payload
    }

    @Action({ rawError: true })
    public resetStore() {
        this.updateGettingStartedData(JSON.parse(JSON.stringify(this.defaultGettingStarted)));
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updateHealthInsuranceInformation(this.defaultHealthInformationData);
        this.updateDiagnosisInformation(this.defaultDiagnosisInformationData);
        this.updatePrescriberInformation(this.defaultPrescriberInformation);
        this.updatePharmacyPrescriptionInformation(this.defaultPharmacyPrescriptionInformationData);
        this.updateIsPatientAdult('');
        this.updateSurveys([])
    }

    private formatPhone(phone: string): string {
        return phone.replace(/\D/g, '');
    }

    private mapAddress(address: IAddress): IAddress {
        return {
            addressOne: address.addressOne,
            addressTwo: address.addressTwo,
            city: address.city,
            state: address.state,
            zip: address.zip,
        };
    }

    private mapCaregiver(caregiver: string | boolean): ICaregiver | null {
        return caregiver
            ? {
                  firstName: this.patientInformation.caregiverFirstName,
                  lastName: this.patientInformation.caregiverFirstName,
                  dateOfBirth: this.patientInformation.dateOfBirth,
                  relationshipToPatient: this.patientInformation.relationShipToPatient,
                  phoneNumber: this.formatPhone(this.patientInformation.caregiverPhone),
                  email: this.patientInformation.caregiverEmail,
              }
            : null;
    }

    private mapPrescribers(): IPrescriberAPI {
        return {
            deaNumber: '',
            firstName: this.prescriberInformation.firstName,
            lastName: this.prescriberInformation.lastName,
            middleName: '',
            npi: this.prescriberInformation.prescriberNpi,
            ptan: '',
            stateLicenseId: '',
            suffix: '',
            taxId: this.prescriberInformation.prescriberTaxId || '',
            type: 'Primary Care',
        };
    }

    private mapMedicalPayors(): IMedicalPayorApi {
        return {
            payorId: '',
            payorName: '',
            phoneNumber: '',
            faxNumber: '',
            groupNumber: '',
            policyNumber: '',
            planEffectiveDate: '',

            planExpirationDate: '',
            planTerminationDate: '',
            policyName: '',
            policyHolderFirstName: '',
            policyHolderLastName: '',
            insurancePriority: 'Primary',
            relationshipToPatient: '',
            address: {},
        };
    }

    private mapPharmacyPayors(): IPharmacyPayorApi {
        return {
            payorId: '',
            payorName: this.healthInsuranceInformation.memberName,
            phoneNumber: this.healthInsuranceInformation.insuranceCompanyPhone,
            faxNumber: '',
            groupNumber: this.healthInsuranceInformation.rxGroup,
            policyNumber: this.healthInsuranceInformation.rxId,
            bin: this.healthInsuranceInformation.rxBin,
            pcn: this.healthInsuranceInformation.rxPcn,
            insuranceState: '',
            pbmName: this.healthInsuranceInformation.rxCardName,
            rxMemberId: this.healthInsuranceInformation.memberId,
            planEffectiveDate: '',
            planExpirationDate: '',
            planTerminationDate: '',
            policyHolderFirstName: '',
            policyHolderLastName: '',
            insurancePriority: 'Primary',
            relationshipToPatient: '',
            policyName: this.healthInsuranceInformation.rxInsurance,
            address: {},
        };
    }

    private mapSites(): ISiteApi {
        const {
            officeContactFax,
            officeContactPhone,
            addressLineOne,
            addressLineTwo,
            prescriberCity,
            prescriberState,
            prescriberZipCode,
        } = this.prescriberInformation;

        const addressObj = {
            addressOne: addressLineOne,
            addressTwo: addressLineTwo,
            city: prescriberCity || '',
            state: prescriberState || '',
            zip: prescriberZipCode || '',
        };
        return {
            contactName: this.prescriberInformation.officeContactName,
            description: '',
            faxNumber: this.formatPhone(officeContactFax || ''),
            id: '',
            name: this.prescriberInformation.siteName,
            phoneNumber: this.formatPhone(officeContactPhone || ''),
            type: '',
            address: formatAddress(addressObj),
        };
    }

    private mapSocSites(): ISocSiteApi {
        const {
            siteContactName,
            infusionSiteName,
            siteContactPhone,
            siteContactFax,
            siteStreetAddress,
            siteSuite,
            siteCity,
            siteState,
            siteZipCode,
        } = this.diagnosisInformation;
        return {
            contactName: siteContactName,
            description: '',
            faxNumber: siteContactFax,
            id: '',
            name: infusionSiteName,
            phoneNumber: siteContactPhone,
            type: '',
            address: {
                addressOne: siteStreetAddress,
                addressTwo: siteSuite,
                city: siteCity,
                state: siteState,
                zip: siteZipCode,
            },
        };
    }

    private getSupportServices() {
        const supportServices: string[] = [];
        if (this.gettingStarted.allSupportServices) {
            supportServices.push(
                SupportTypes.BenefitsInvestigation,
                SupportTypes.InjectionSupport,
                SupportTypes.Copay,
                SupportTypes.Glatopaject
            );
        } else {
            if (this.gettingStarted.benefitsInvestigation) {
                supportServices.push(SupportTypes.BenefitsInvestigation);
            }
            if (this.gettingStarted.copayServices) {
                supportServices.push(SupportTypes.Copay);
            }
            if (this.gettingStarted.injectionSupport) {
                supportServices.push(SupportTypes.InjectionSupport);
            }
            if (this.gettingStarted.glatopaInject) {
                supportServices.push(SupportTypes.Glatopaject);
            }
        }
        return supportServices;
    }

    public formatAPIPayload() {
        return {
            callingTransactionID: '',
            callingTransactionType: 'Enrollment',
            caseSource: 'Website Enrollment',
            programCode: 'HYRI',
            productFamily: 'Glatopa',
            diagnosisCodes: this.diagnosisInformation.selectedDiagnoses,
            preferredPharmacy: [this.pharmacyPrescriptionInformation.preferredPharmacy ?? ''],
            servicesNeeded: this.getSupportServices(),
            mercalisHubId: '',
            thirdPartyHubId: '',
            thirdPartyCaseId: '',
            patient: {
                firstName: this.patientInformation.firstName,
                lastName: this.patientInformation.lastName,
                middleName: this.patientInformation.middleName,
                dateOfBirth: this.patientInformation.dateOfBirth,
                cellPhone: this.formatPhone(this.patientInformation.patientPhone),
                emailAddress: this.patientInformation.email,
                emailOutput: true,
                gender: this.patientInformation.gender,
                homePhone: this.formatPhone(this.patientInformation.patientHomePhone),
                mailOptOut: true,
                phoneOptOut: true,
                preferredCommunicationType: 'Email',
                preferredLanguage: this.gettingStarted.language,
                race: '',
                residentOfUS: true,
                suffix: 'I',
                textOptOut: true,
                videoOptOut: true,
                voicemailOptOut: true,
                workPhone: '',
                address: this.mapAddress(this.patientInformation),
                caregiverContact: this.mapCaregiver(this.patientInformation.IsPatientAgeBetween6And17),
            },
            medicalPayors: [this.mapMedicalPayors()],
            pharmacyPayors: [this.mapPharmacyPayors()],
            // socSites: [this.mapSocSites()],
            sites: [this.mapSites()],
            prescribers: [this.mapPrescribers()],
            prescriptions: this.pharmacyPrescriptionInformation.allPrescriptions,
            consent: [
                {
                    consentEndDate: '',
                    consentParty: 'HCP',
                    consentStartDate: this.pharmacyPrescriptionInformation.todaysDate,
                    consentType: 'HIPAA Consent',
                    responseType: 'Granted',
                },
            ],
            surveys: this.surveys,
            attachments: [],
        };
    }

    @Action({})
    async submitClsEnrollment(payload: any) {
        try {
            const axios: AxiosInstance = (this.edgeApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/cls/ClsEnrollment/enroll',
                method: 'POST',
                data: payload,
                headers: {
                    'x-program-id': this.settings.clsProgramId!,
                },
            };
            await Promise.all([
                (this.edgeApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.edgeApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.edgeApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
            return data.data && data.success;
        } catch {
            return false;
        }
    }
}
